import { MapPin, Phone, Mail, TrendingUp, Facebook, Instagram, MessageCircle, ChevronRight, Clock, Shield, Calendar, Loader2, Award, ExternalLink } from "lucide-react";
const lastUpdatedFormatter = new Intl.DateTimeFormat("it-IT", {
  day: "numeric",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});
import { Link, useLocation } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useState } from "react";
import type { Quotations } from "@shared/schema";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import logoMonochrome from "@/assets/universo-oro-logo-monochrome-optimized.png";
import { useSettings, getSettingWithFallback } from "@/hooks/use-cms";
import { normalizePhone } from "@/lib/phone-utils";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { QuotationStatusIndicator } from "./quotation-status-banner";

export function Footer() {
  const [gdprConsent, setGdprConsent] = useState(false);
  const [email, setEmail] = useState("");
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  const newsletterMutation = useMutation({
    mutationFn: async (data: { email: string }) => {
      return apiRequest("POST", "/api/newsletter", data);
    },
    onSuccess: () => {
      setLocation("/newsletter/grazie");
    },
    onError: (error: any) => {
      const message = error?.message || "Errore durante l'iscrizione";
      toast({
        title: "Errore",
        description: message.includes("già registrata") 
          ? "Questa email è già iscritta alla newsletter" 
          : message,
        variant: "destructive",
      });
    },
  });

  const handleNewsletterSubmit = () => {
    if (!email || !email.includes("@")) {
      toast({
        title: "Errore",
        description: "Inserisci un indirizzo email valido",
        variant: "destructive",
      });
      return;
    }
    if (!gdprConsent) {
      toast({
        title: "Consenso richiesto",
        description: "Devi accettare l'informativa sulla privacy per iscriverti",
        variant: "destructive",
      });
      return;
    }
    newsletterMutation.mutate({ email });
  };
  
  const { data: quotations } = useQuery<Quotations>({
    queryKey: ["/api/quotations"],
  });
  
  // Fetch CMS settings for footer
  const { data: settings } = useSettings([
    'phone_main',
    'phone_tollfree',
    'whatsapp_general',
    'whatsapp_watches',
    'email_contact',
    'store_hours',
    'address_rome',
    'address_arezzo',
    'business_name',
    'vat_number',
    'oam_oco',
    'oam_opo',
    'hours_monday',
    'hours_tuesday',
    'hours_saturday',
    'hours_sunday',
    'instagram',
    'facebook',
    'founding_year',
    'canonical_domain',
    'secondary_domain',
    'shop_domain'
  ]);

  const foundingYear = getSettingWithFallback(settings, 'founding_year', '').toString();
  const yearsOfExperience = (() => {
    const parsed = parseInt(foundingYear, 10);
    if (!Number.isFinite(parsed) || parsed <= 1900) return null;
    return new Date().getFullYear() - parsed;
  })();
  const canonicalDomain = getSettingWithFallback(settings, 'canonical_domain', '').toString();
  const secondaryDomain = getSettingWithFallback(settings, 'secondary_domain', '').toString();
  const shopDomain = getSettingWithFallback(settings, 'shop_domain', '').toString();
  const domains = [canonicalDomain, secondaryDomain].filter(Boolean);
  const footerPhone = normalizePhone(getSettingWithFallback(settings, 'phone_main', '').toString());
  const footerEmail = getSettingWithFallback(settings, 'email_contact', '').toString();

  return (
    <footer className="bg-[hsl(var(--footer-bg))] text-[hsl(var(--footer-fg))]">
      {/* Live Quotations Bar */}
      <div className="bg-primary text-primary-foreground py-3">
        <div className="container mx-auto max-w-7xl px-4">
          <div className="flex items-center justify-center gap-4 text-sm flex-wrap">
            <div className="flex items-center gap-1.5">
              <TrendingUp className="h-4 w-4" />
              <span>Quotazioni Live:</span>
            </div>
            <div className="flex items-center gap-1">
              <span>ORO 24K:</span>
              <span className="font-semibold">{quotations?.rawMarketPrices?.gold24k?.toFixed(2) || "---"} €/g</span>
            </div>
            <div className="flex items-center gap-1">
              <span>ARGENTO:</span>
              <span className="font-semibold">{quotations?.rawMarketPrices?.silver999?.toFixed(3) || "---"} €/g</span>
            </div>
            <QuotationStatusIndicator status={quotations?.status} testIdSuffix="footer" />
            {quotations?.lastUpdated && (
              <div className="flex items-center gap-1.5 text-primary-foreground/90">
                <Calendar className="h-4 w-4" />
                <span>
                  Aggiornato: {lastUpdatedFormatter.format(new Date(quotations.lastUpdated))}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="container mx-auto max-w-7xl px-4 py-8">
        {/* Newsletter Section - Full Width (Prima) */}
        <div className="mb-8">
          <div className="bg-[hsl(var(--header-bg))] border border-primary/40 rounded-md p-6">
            <div className="flex flex-col gap-4">
              {/* Header row */}
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4 text-center md:text-left">
                  <div className="hidden md:flex items-center justify-center w-12 h-12 border-2 border-primary/40 rounded-full">
                    <Mail className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-sm text-subheading text-[hsl(var(--uo-cream))] uppercase mb-1">Newsletter</h3>
                    <p className="text-xs text-[hsl(var(--footer-fg))]/80">
                      Ricevi aggiornamenti su quotazioni oro e opportunità esclusive
                    </p>
                  </div>
                </div>
                <div className="flex gap-3 w-full md:w-auto">
                  <Input 
                    type="email" 
                    placeholder="La tua email" 
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleNewsletterSubmit()}
                    className="flex-1 md:w-72 h-10 text-sm bg-white/10 border-primary/40 text-[hsl(var(--uo-cream))] placeholder:text-[hsl(var(--uo-cream))]/60 focus:border-primary focus:bg-white/15"
                    data-testid="input-newsletter"
                  />
                  <Button 
                    size="default" 
                    className="uppercase tracking-wide font-semibold" 
                    data-testid="button-newsletter"
                    disabled={!gdprConsent || newsletterMutation.isPending}
                    onClick={handleNewsletterSubmit}
                  >
                    {newsletterMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      "Iscriviti"
                    )}
                  </Button>
                </div>
              </div>
              
              {/* GDPR consent row */}
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3 pt-2 border-t border-primary/20">
                <div className="flex items-start gap-3">
                  <Checkbox 
                    id="gdpr-consent"
                    checked={gdprConsent}
                    onCheckedChange={(checked) => setGdprConsent(checked === true)}
                    className="mt-0.5 border-primary/50 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                    data-testid="checkbox-newsletter-gdpr"
                  />
                  <label 
                    htmlFor="gdpr-consent" 
                    className="text-[11px] text-[hsl(var(--footer-fg))]/80 leading-relaxed cursor-pointer"
                  >
                    Acconsento a ricevere la newsletter di Universo Oro. Posso cancellarmi in qualsiasi momento.
                  </label>
                </div>
                <Link href="/privacy" data-testid="link-newsletter-privacy">
                  <span className="inline-flex items-center min-h-11 md:min-h-6 text-[11px] text-gold-light hover:underline whitespace-nowrap cursor-pointer">
                    Leggi la Privacy Policy
                  </span>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="flex items-center gap-4 mb-8">
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          
          {/* Column 1: Company Info */}
          <div>
            <div className="mb-3">
              <div className="logo-container inline-block">
                <img 
                  src={logoMonochrome} 
                  alt="UNIVERSO ORO Logo" 
                  className="h-10 w-auto"
                  width={161}
                  height={80}
                />
              </div>
            </div>
            
            <p className="text-[11px] mb-3 leading-relaxed text-[hsl(var(--footer-fg))]/80">
              La tua fiducia è il nostro patrimonio più prezioso. Siamo al tuo fianco con professionalità, trasparenza e dedizione{yearsOfExperience ? ` da oltre ${yearsOfExperience} anni` : ' da lunga data'}.
            </p>

            <div className="flex gap-2">
              <a 
                href={getSettingWithFallback(settings, 'facebook', 'https://www.facebook.com/universooro')} 
                target="_blank" 
                rel="noopener noreferrer"
                className="h-11 w-11 md:h-8 md:w-8 flex items-center justify-center bg-[hsl(var(--uo-olive))] rounded-full hover:bg-primary transition-colors"
                aria-label="Facebook"
              >
                <Facebook className="h-3.5 w-3.5" />
              </a>
              <a 
                href={getSettingWithFallback(settings, 'instagram', 'https://www.instagram.com/universooro')} 
                target="_blank" 
                rel="noopener noreferrer"
                className="h-11 w-11 md:h-8 md:w-8 flex items-center justify-center bg-[hsl(var(--uo-olive))] rounded-full hover:bg-primary transition-colors"
                aria-label="Instagram"
              >
                <Instagram className="h-3.5 w-3.5" />
              </a>
              <a 
                href={`https://wa.me/${normalizePhone(getSettingWithFallback(settings, 'whatsapp_general', '3405517662')).whatsapp}`}
                target="_blank" 
                rel="noopener noreferrer"
                className="h-11 w-11 md:h-8 md:w-8 flex items-center justify-center bg-[hsl(var(--uo-olive))] rounded-full hover:bg-primary transition-colors"
                aria-label="WhatsApp"
              >
                <MessageCircle className="h-3.5 w-3.5" />
              </a>
            </div>
          </div>

          {/* Column 2: Services */}
          <div>
            <h3 className="text-[11px] text-subheading text-[hsl(var(--uo-cream))] mb-3 uppercase">I Nostri Servizi</h3>
            <ul className="space-y-1.5">
              <li>
                <Link href="/orologeria/in-vendita" data-testid="link-footer-orologi">
                  <span className="flex items-center gap-1 text-[11px] hover:text-gold-light transition-colors cursor-pointer min-h-11 md:min-h-6 py-1">
                    <ChevronRight className="h-2.5 w-2.5" />
                    Orologeria di Lusso
                  </span>
                </Link>
              </li>
              <li>
                <Link href="/gioielleria" data-testid="link-footer-gioielleria">
                  <span className="flex items-center gap-1 text-[11px] hover:text-gold-light transition-colors cursor-pointer min-h-11 md:min-h-6 py-1">
                    <ChevronRight className="h-2.5 w-2.5" />
                    Gioielleria
                  </span>
                </Link>
              </li>
              <li>
                <Link href="/compro-oro-argento/valutazione" data-testid="link-footer-compro-oro">
                  <span className="flex items-center gap-1 text-[11px] hover:text-gold-light transition-colors cursor-pointer min-h-11 md:min-h-6 py-1">
                    <ChevronRight className="h-2.5 w-2.5" />
                    Compro Oro e Argento
                  </span>
                </Link>
              </li>
              <li>
                <Link href="/investire-in-oro" data-testid="link-footer-investimenti">
                  <span className="flex items-center gap-1 text-[11px] hover:text-gold-light transition-colors cursor-pointer min-h-11 md:min-h-6 py-1">
                    <ChevronRight className="h-2.5 w-2.5" />
                    Investire in Oro Fisico
                  </span>
                </Link>
              </li>
            </ul>
          </div>

          {/* Column 3: Information */}
          <div>
            <h3 className="text-[11px] text-subheading text-[hsl(var(--uo-cream))] mb-3 uppercase">Informazioni</h3>
            <ul className="space-y-1.5">
              <li>
                <Link href="/servizi" data-testid="link-footer-servizi">
                  <span className="flex items-center gap-1 text-[11px] hover:text-primary transition-colors cursor-pointer">
                    <ChevronRight className="h-2.5 w-2.5" />
                    Tutti i Servizi
                  </span>
                </Link>
              </li>
              <li>
                <Link href="/chi-siamo" data-testid="link-footer-chi-siamo">
                  <span className="flex items-center gap-1 text-[11px] hover:text-gold-light transition-colors cursor-pointer min-h-11 md:min-h-6 py-1">
                    <ChevronRight className="h-2.5 w-2.5" />
                    Chi Siamo
                  </span>
                </Link>
              </li>
              <li>
                <Link href="/dove-siamo" data-testid="link-footer-dove-siamo">
                  <span className="flex items-center gap-1 text-[11px] hover:text-gold-light transition-colors cursor-pointer min-h-11 md:min-h-6 py-1">
                    <ChevronRight className="h-2.5 w-2.5" />
                    Dove Siamo
                  </span>
                </Link>
              </li>
              <li>
                <Link href="/privacy" data-testid="link-footer-privacy">
                  <span className="flex items-center gap-1 text-[11px] hover:text-gold-light transition-colors cursor-pointer min-h-11 md:min-h-6 py-1">
                    <ChevronRight className="h-2.5 w-2.5" />
                    Privacy Policy
                  </span>
                </Link>
              </li>
              <li>
                <Link href="/termini" data-testid="link-footer-termini">
                  <span className="flex items-center gap-1 text-[11px] hover:text-gold-light transition-colors cursor-pointer min-h-11 md:min-h-6 py-1">
                    <ChevronRight className="h-2.5 w-2.5" />
                    Termini e Condizioni
                  </span>
                </Link>
              </li>
              <li>
                <Link href="/garanzie" data-testid="link-footer-garanzie">
                  <span className="flex items-center gap-1 text-[11px] hover:text-gold-light transition-colors cursor-pointer min-h-11 md:min-h-6 py-1">
                    <ChevronRight className="h-2.5 w-2.5" />
                    Garanzie e Certificazioni
                  </span>
                </Link>
              </li>
              <li>
                <Link href="/politica-resi" data-testid="link-footer-politica-resi">
                  <span className="flex items-center gap-1 text-[11px] hover:text-gold-light transition-colors cursor-pointer min-h-11 md:min-h-6 py-1">
                    <ChevronRight className="h-2.5 w-2.5" />
                    Politica Resi
                  </span>
                </Link>
              </li>
              <li>
                <Link href="/whistleblowing" data-testid="link-footer-whistleblowing">
                  <span className="flex items-center gap-1 text-[11px] hover:text-gold-light transition-colors cursor-pointer min-h-11 md:min-h-6 py-1">
                    <ChevronRight className="h-2.5 w-2.5" />
                    Whistleblowing
                  </span>
                </Link>
              </li>
            </ul>

            {/* Certification Badges - Premium Style with Brand Colors */}
            <div className="mt-4 space-y-2">
              <p className="text-[10px] text-gold-light font-medium uppercase tracking-wider flex items-center gap-1.5">
                <Shield className="h-3.5 w-3.5" />
                Operatore Certificato
              </p>
              <div className="grid grid-cols-1 gap-2">
                {/* OAM Badge - using header-bg like newsletter and legal footer */}
                <div className="flex items-center gap-2.5 px-3 py-2 rounded border border-primary/40 bg-[hsl(var(--header-bg))]">
                  <div className="flex-shrink-0">
                    <Shield className="h-5 w-5 text-primary" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[12px] text-[hsl(var(--uo-cream)/0.8)] leading-tight uppercase tracking-wider">Elenco OAM - Compro Oro</p>
                    <p className="text-[12px] text-gold-light font-semibold tracking-wide">{getSettingWithFallback(settings, 'oam_oco', '')}</p>
                  </div>
                </div>
                {/* OPO Badge - using header-bg like newsletter and legal footer */}
                <div className="flex items-center gap-2.5 px-3 py-2 rounded border border-primary/40 bg-[hsl(var(--header-bg))]">
                  <div className="flex-shrink-0">
                    <Award className="h-5 w-5 text-primary" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[12px] text-[hsl(var(--uo-cream)/0.8)] leading-tight uppercase tracking-wider">Elenco OPO - Operatori Professionali</p>
                    <p className="text-[12px] text-gold-light font-semibold tracking-wide">{getSettingWithFallback(settings, 'oam_opo', '')}</p>
                  </div>
                </div>
                {/* OAM Verification Link */}
                <a 
                  href="https://www.organismo-am.it/elenchi-registri/operatori-compro-oro/ricerca"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-1.5 px-3 py-2 min-h-11 md:min-h-6 text-[10px] text-gold-light hover:text-[hsl(var(--uo-cream))] transition-colors"
                  data-testid="link-oam-verification"
                >
                  <ExternalLink className="h-3 w-3" />
                  <span>Verifica iscrizione su OAM</span>
                </a>
              </div>
            </div>
          </div>

          {/* Column 4: Contact & Newsletter */}
          <div>
            <h3 className="text-[11px] text-subheading text-[hsl(var(--uo-cream))] mb-3 uppercase">Contatti</h3>
            
            <ul className="space-y-2 mb-4">
              <li className="flex items-start gap-1.5">
                <MapPin className="h-3.5 w-3.5 text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-[10px] font-medium text-[hsl(var(--uo-cream))]">Sede Principale</p>
                  <p className="text-[10px] text-[hsl(var(--footer-fg))]/80">{getSettingWithFallback(settings, 'address_rome', 'Viale Eritrea, 88/94, 00199 Roma')}</p>
                </div>
              </li>
              <li className="flex items-start gap-1.5">
                <Phone className="h-3.5 w-3.5 text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-[10px] font-medium text-[hsl(var(--uo-cream))]">Telefono</p>
                  {getSettingWithFallback(settings, 'phone_main', '') && (
                    <p className="text-[10px] text-[hsl(var(--footer-fg))]/80">{getSettingWithFallback(settings, 'phone_main', '')}</p>
                  )}
                  {getSettingWithFallback(settings, 'phone_tollfree', '') && (
                    <p className="text-[10px] text-gold-light">Numero Verde: {getSettingWithFallback(settings, 'phone_tollfree', '')}</p>
                  )}
                </div>
              </li>
              <li className="flex items-start gap-1.5">
                <Mail className="h-3.5 w-3.5 text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-[10px] font-medium text-[hsl(var(--uo-cream))]">Email</p>
                  {footerEmail && (
                    <p className="text-[10px] text-[hsl(var(--footer-fg))]/80">{footerEmail}</p>
                  )}
                </div>
              </li>
              <li className="flex items-start gap-1.5">
                <Clock className="h-3.5 w-3.5 text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-[10px] font-medium text-[hsl(var(--uo-cream))]">Orari</p>
                  <p className="text-[10px] text-[hsl(var(--footer-fg))]/80">Mar-Sab: {getSettingWithFallback(settings, 'hours_tuesday', '10:00-13:00 | 15:30-19:30')}</p>
                  <p className="text-[10px] text-[hsl(var(--footer-fg))]/80">Lunedì: {getSettingWithFallback(settings, 'hours_monday', 'Chiuso')} | Domenica: {getSettingWithFallback(settings, 'hours_sunday', 'Chiuso')}</p>
                </div>
              </li>
            </ul>

          </div>
        </div>

      </div>

      {/* Copyright Bar with Legal Links */}
      <div className="bg-[hsl(var(--header-bg))] border-t border-primary/20">
        <div className="container mx-auto max-w-7xl px-4 py-3">
          <div className="flex flex-col md:flex-row items-center justify-between gap-2">
            <p className="text-[10px] text-[hsl(var(--footer-fg))]/75 text-center md:text-left">
              © {new Date().getFullYear()} {getSettingWithFallback(settings, 'business_name', 'Universo Oro')}{getSettingWithFallback(settings, 'vat_number', '') && ` - P.IVA ${getSettingWithFallback(settings, 'vat_number', '')}`} - Tutti i diritti riservati
            </p>
            <div className="flex items-center gap-4">
              <Link href="/privacy" data-testid="link-copyright-privacy">
                <span className="inline-flex items-center min-h-11 md:min-h-6 text-[10px] text-[hsl(var(--footer-fg))]/75 hover:text-gold-light transition-colors cursor-pointer">
                  Privacy
                </span>
              </Link>
              <Link href="/cookie" data-testid="link-copyright-cookie">
                <span className="inline-flex items-center min-h-11 md:min-h-6 text-[10px] text-[hsl(var(--footer-fg))]/75 hover:text-gold-light transition-colors cursor-pointer">
                  Cookie
                </span>
              </Link>
              <Link href="/termini" data-testid="link-copyright-termini">
                <span className="inline-flex items-center min-h-11 md:min-h-6 text-[10px] text-[hsl(var(--footer-fg))]/75 hover:text-gold-light transition-colors cursor-pointer">
                  Termini
                </span>
              </Link>
              <Link href="/sicurezza-trasparenza" data-testid="link-copyright-sicurezza">
                <span className="inline-flex items-center min-h-11 md:min-h-6 text-[10px] text-[hsl(var(--footer-fg))]/75 hover:text-gold-light transition-colors cursor-pointer">
                  Sicurezza
                </span>
              </Link>
              <Link href="/mappa-del-sito" data-testid="link-copyright-sitemap">
                <span className="inline-flex items-center min-h-11 md:min-h-6 text-[10px] text-[hsl(var(--footer-fg))]/75 hover:text-gold-light transition-colors cursor-pointer">
                  Mappa del Sito
                </span>
              </Link>
              <Link href="/whistleblowing" data-testid="link-copyright-whistleblowing">
                <span className="inline-flex items-center min-h-11 md:min-h-6 text-[10px] text-[hsl(var(--footer-fg))]/75 hover:text-gold-light transition-colors cursor-pointer">
                  Whistleblowing
                </span>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Dichiarazione Domini e Identità - Google Security Compliance */}
      <div className="bg-[hsl(var(--header-bg))] border-t border-primary/10">
        <div className="container mx-auto max-w-7xl px-4 py-4">
          <div className="text-center space-y-3">
            {/* Identità Aziendale */}
            <div className="flex flex-wrap items-center justify-center gap-4 text-[10px]">
              <span className="text-[hsl(var(--footer-fg))]/70 font-medium">
                {getSettingWithFallback(settings, 'business_name', 'UNIVERSO ORO S.r.l.')}
              </span>
              <span className="text-[hsl(var(--footer-fg))]/40">|</span>
              <span className="text-[hsl(var(--footer-fg))]/75">Roma, Italia</span>
              {footerPhone.tel && (
                <>
                  <span className="text-[hsl(var(--footer-fg))]/40">|</span>
                  <a href={`tel:${footerPhone.tel}`} className="text-gold-light hover:underline inline-flex items-center min-h-11 md:min-h-6">
                    {footerPhone.display}
                  </a>
                </>
              )}
              {footerEmail && (
                <>
                  <span className="text-[hsl(var(--footer-fg))]/40">|</span>
                  <a href={`mailto:${footerEmail}`} className="text-gold-light hover:underline inline-flex items-center min-h-11 md:min-h-6">
                    {footerEmail}
                  </a>
                </>
              )}
            </div>

            {/* Domini */}
            {(domains.length > 0 || shopDomain) && (
              <>
                <p className="text-[10px] text-[hsl(var(--footer-fg))]/75">
                  I seguenti domini sono di proprietà e gestione della stessa società:
                </p>
                <div className="flex flex-wrap items-center justify-center gap-3 text-[10px]">
                  {domains.map((domain, index) => (
                    <span key={domain} className="flex items-center gap-3">
                      {index > 0 && <span className="text-[hsl(var(--footer-fg))]/30">•</span>}
                      <a 
                        href={`https://www.${domain}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-gold-light hover:text-gold-light underline inline-flex items-center min-h-11 md:min-h-6"
                      >
                        www.{domain}
                      </a>
                    </span>
                  ))}
                  {shopDomain && (
                    <span className="flex items-center gap-3">
                      {domains.length > 0 && <span className="text-[hsl(var(--footer-fg))]/30">•</span>}
                      <span className="text-[hsl(var(--footer-fg))]/75">{shopDomain}</span>
                    </span>
                  )}
                </div>
              </>
            )}
            <p className="text-[10px] text-[hsl(var(--footer-fg))]/75">
              Tutti i servizi sono erogati esclusivamente da {getSettingWithFallback(settings, 'business_name', 'Universo Oro')}.
            </p>
          </div>
        </div>
      </div>

      {/* Legal Disclaimers Bar */}
      <div className="bg-[hsl(var(--header-bg))] border-t border-primary/10">
        <div className="container mx-auto max-w-7xl px-4 py-3">
          <p className="text-[12px] text-[hsl(var(--footer-fg))]/75 leading-relaxed text-center">
            {getSettingWithFallback(settings, 'business_name', 'UNIVERSO ORO S.r.l.')} opera nel settore della compravendita di preziosi di seconda mano e pre-owned. 
            I marchi e i loghi presenti su questo sito appartengono ai rispettivi proprietari e sono utilizzati 
            esclusivamente a scopo identificativo per facilitare la navigazione degli utenti. {getSettingWithFallback(settings, 'business_name', 'UNIVERSO ORO S.r.l.')} 
            non è affiliata, autorizzata o concessionaria di alcuno dei marchi citati. Le quotazioni dell'oro, 
            dell'argento e degli altri metalli preziosi riportate sul sito hanno carattere puramente indicativo 
            e non costituiscono offerta al pubblico. Ai sensi della normativa vigente, la valutazione definitiva 
            e la conferma delle transazioni possono avvenire esclusivamente presso la nostra sede.
          </p>
        </div>
      </div>
    </footer>
  );
}
